(() => {
  if (window.MettaCategoryContent) return;
  const instances = new WeakMap();
  const kinds = ['intro', 'ingredients', 'goals', 'preparation', 'faq'];
  const families = ['original', 'protein', 'upsell', 'oatmeal'];
  const read = (root, selector) => JSON.parse(root.querySelector(selector)?.textContent || '{}');

  function mount(root) {
    if (instances.has(root)) return;
    const host = root.querySelector('[data-v2-story-host]');
    if (!host) return;
    let config, catalog;
    try { config = read(root, '[data-v2-content-config]'); catalog = read(root, '[data-v2-catalog]'); } catch (_) { return; }
    const initial = root.dataset.initialFamily;
    const editorialFamilies = ['original', 'protein'].filter(family => kinds.every(kind =>
      document.querySelectorAll(`[data-v2-editorial-kind="${kind}"][data-v2-content-family="${family}"]`).length === 1
    ));
    const editorialInitial = editorialFamilies.length === 1 ? editorialFamilies[0] : undefined;
    const slots = new Map();
    for (const kind of kinds) {
      const node = editorialInitial && document.querySelector(`[data-v2-editorial-kind="${kind}"][data-v2-content-family="${editorialInitial}"]`);
      const wrapper = node?.closest('.shopify-section');
      if (node && wrapper) slots.set(kind, { node, original: node, wrapper, wasHidden: wrapper.hidden });
    }
    const cached = new Map();
    if (editorialInitial && slots.size === kinds.length) cached.set(editorialInitial, new Map([...slots].map(([kind, slot]) => [kind, slot.node])));
    const pending = new Map();
    const requests = new Set();
    const listeners = new AbortController();
    let current = '', revision = 0, disposed = false;
    const status = document.createElement('div');
    status.className = 'v2-content-status';
    status.dataset.v2ContentStatus = '';
    status.hidden = true;
    const message = document.createElement('p');
    message.setAttribute('role', 'status');
    const retry = document.createElement('button');
    retry.type = 'button';
    retry.className = 'v2-button';
    retry.dataset.v2ContentRetry = '';
    retry.textContent = config.retry || '';
    status.append(message, retry);
    host.append(status);

    function pause(node) { node.querySelectorAll('video').forEach(video => video.pause()); }
    function hideContent() {
      for (const { wrapper } of slots.values()) { pause(wrapper); wrapper.hidden = true; }
      host.querySelectorAll('[data-v2-category-story]').forEach(story => { pause(story); story.hidden = true; });
    }
    function announce(text, canRetry) {
      message.textContent = text || '';
      retry.hidden = !canRetry;
      status.hidden = !text;
      host.setAttribute('aria-busy', String(Boolean(text) && !canRetry));
    }
    function load(family) {
      if (cached.has(family)) return Promise.resolve(cached.get(family));
      if (pending.has(family)) return pending.get(family);
      const promise = (async () => {
        const landing = catalog.families?.find(item => item.key === family)?.landingUrl;
        if (!landing || !config.views?.[family] || slots.size !== kinds.length) throw new Error('Missing category source');
        const url = new URL(landing, location.origin);
        if (url.origin !== location.origin) throw new Error('Invalid category source');
        const localeRoot = typeof config.localeRoot === 'string' ? config.localeRoot.replace(/^\/+|\/+$/g, '') : '';
        if (localeRoot && url.pathname.startsWith('/products/')) url.pathname = '/' + localeRoot + url.pathname;
        new URLSearchParams(location.search).forEach((value, key) => url.searchParams.set(key, value));
        ['family', 'category', 'section_id', 'sections'].forEach(key => url.searchParams.delete(key));
        url.searchParams.set('view', config.views[family]);
        url.hash = '';
        const request = new AbortController();
        requests.add(request);
        const timeout = setTimeout(() => request.abort(), 10000);
        try {
          const response = await fetch(url.href, { credentials: 'same-origin', signal: request.signal });
          if (!response.ok) throw new Error('Category request failed');
          const html = await response.text();
          const remote = new DOMParser().parseFromString(html, 'text/html');
          const pdp = remote.querySelector('[data-v2-pdp]');
          if (!pdp || pdp.dataset.initialFamily !== family || !root.dataset.themeId || pdp.dataset.themeId !== root.dataset.themeId) throw new Error('Category theme mismatch');
          const language = document.documentElement.lang.trim().toLowerCase();
          const remoteLanguage = remote.documentElement.lang.trim().toLowerCase();
          if (language && remoteLanguage && language !== remoteLanguage) throw new Error('Category language mismatch');
          const nodes = new Map();
          for (const kind of kinds) {
            const matches = remote.querySelectorAll(`[data-v2-editorial-kind="${kind}"][data-v2-content-family="${family}"]`);
            if (matches.length !== 1) throw new Error('Incomplete category content');
            const node = document.importNode(matches[0], true);
            node.querySelectorAll('script').forEach(script => script.remove());
            nodes.set(kind, node);
          }
          if (!disposed) cached.set(family, nodes);
          return nodes;
        } finally { clearTimeout(timeout); requests.delete(request); }
      })();
      pending.set(family, promise);
      promise.then(() => pending.delete(family), () => pending.delete(family));
      return promise;
    }
    function infoRow(family) {
      return [...root.querySelectorAll('[data-v2-row]')].find(row => row.dataset.family === family && row.dataset.available !== 'false' && row.querySelector('[data-v2-info]'));
    }
    async function activate(family) {
      if (disposed || !families.includes(family)) return;
      current = family;
      const token = ++revision;
      hideContent();
      announce('', false);
      if (family === 'upsell' || family === 'oatmeal') {
        let story = host.querySelector(`[data-v2-category-story="${family}"]`);
        if (!story) {
          const template = root.querySelector(`[data-v2-story-template="${family}"]`);
          if (template) host.append(template.content.cloneNode(true));
          story = host.querySelector(`[data-v2-category-story="${family}"]`);
        }
        if (story) {
          story.hidden = false;
          story.querySelectorAll('[data-v2-story-details]').forEach(button => { button.disabled = !infoRow(family); });
          root.dispatchEvent(new CustomEvent('v2:content-ready', { bubbles: true }));
        }
        return;
      }
      // Embedded builders on legacy landing pages have no V2 editorial slots.
      if (!slots.size) return;
      if (!cached.has(family)) announce(config.loading, false);
      try {
        const nodes = await load(family);
        if (disposed || token !== revision) return;
        for (const [kind, slot] of slots) {
          const next = nodes.get(kind);
          if (next !== slot.node) { slot.node.replaceWith(next); slot.node = next; }
          slot.node.hidden = false;
          slot.wrapper.hidden = false;
          window.MettaEditorial?.init(next);
        }
        announce('', false);
        root.dispatchEvent(new CustomEvent('v2:content-ready', { bubbles: true }));
      } catch (_) {
        if (!disposed && token === revision) announce(config.error, true);
      }
    }
    retry.addEventListener('click', () => activate(current), { signal: listeners.signal });
    root.addEventListener('v2:family-change', event => activate(event.detail?.family), { signal: listeners.signal });
    host.addEventListener('click', event => {
      const pick = event.target.closest('[data-v2-story-pick]');
      const details = event.target.closest('[data-v2-story-details]');
      if (pick) {
        const category = current === 'upsell' ? 'protein_powder' : current === 'oatmeal' ? 'oatmeal' : 'foodshake';
        root.querySelector(`[data-v2-category="${category}"]`)?.focus({ preventScroll: true });
        root.querySelector('[data-v2-flavor-panel]')?.scrollIntoView({ block: 'start', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      }
      if (details && !details.disabled) infoRow(current)?.querySelector('[data-v2-info]')?.click();
    }, { signal: listeners.signal });
    function dispose() {
      disposed = true;
      revision++;
      listeners.abort();
      requests.forEach(request => request.abort());
      for (const nodes of cached.values()) for (const node of nodes.values()) pause(node);
      for (const slot of slots.values()) {
        if (slot.node !== slot.original && slot.node.parentNode) slot.node.replaceWith(slot.original);
        slot.wrapper.hidden = slot.wasHidden;
      }
      for (const [family, nodes] of cached) {
        if (family !== editorialInitial) for (const node of nodes.values()) window.MettaEditorial?.destroy?.(node);
      }
      host.querySelectorAll('[data-v2-category-story]').forEach(story => { pause(story); story.hidden = true; });
      status.remove();
      instances.delete(root);
    }
    document.addEventListener('shopify:section:unload', event => {
      if (event.target === root || event.target.contains(root)) dispose();
    }, { signal: listeners.signal });
    instances.set(root, { dispose });
    activate(root.dataset.activeFamily || initial);
  }
  function init(scope = document) {
    if (scope.matches?.('[data-v2-pdp]')) mount(scope);
    scope.querySelectorAll('[data-v2-pdp]').forEach(mount);
  }
  window.MettaCategoryContent = { init };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
})();
